import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Search } from 'lucide-react'
import { apiFetch, qs } from '../lib/api'
import { useDebounced } from '../lib/hooks'
import type { ActivityRow, Paged } from '../lib/types'
import { DataTable } from '../components/data-table'
import { Pagination } from '../components/pagination'
import { PageHeader, formatDate } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { Badge, StatusBadge } from '../components/badge'

interface Filters {
	actions: string[]
	actors: { actorType: string; actorId: string | null; actorEmail: string }[]
}

const columns: ColumnDef<ActivityRow, any>[] = [
	{ header: 'When', accessorKey: 'createdAt', cell: ({ getValue }) => <span className="whitespace-nowrap">{formatDate(getValue<string>())}</span> },
	{
		header: 'Actor',
		cell: ({ row }) => (
			<div className="flex min-w-0 items-center gap-2">
				<Badge tone={row.original.actorType === 'admin' ? 'info' : row.original.actorType === 'system' ? 'warning' : 'neutral'}>
					{row.original.actorType}
				</Badge>
				<span className="truncate">{row.original.actorEmail}</span>
			</div>
		),
	},
	{ header: 'Action', accessorKey: 'action', cell: ({ getValue }) => <span className="mono">{getValue<string>()}</span> },
	{ header: 'Summary', accessorKey: 'summary', cell: ({ getValue }) => <span className="block max-w-md truncate">{getValue<string>()}</span> },
	{ header: 'Result', accessorKey: 'status', cell: ({ getValue }) => <StatusBadge value={getValue<string>()} /> },
]

export function LogsPage() {
	const [search, setSearch] = useState('')
	const [action, setAction] = useState('')
	const [actorType, setActorType] = useState('')
	const [status, setStatus] = useState('')
	const [page, setPage] = useState(1)
	const [selected, setSelected] = useState<ActivityRow | null>(null)
	const q = useDebounced(search)

	// Sourced from the backend so the dropdown can never drift from the actions
	// actually being written.
	const filters = useQuery({
		queryKey: ['log-filters'],
		queryFn: () => apiFetch<Filters>('/api/admin/logs/filters'),
	})

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['logs', q, action, actorType, status, page],
		queryFn: () => apiFetch<Paged<ActivityRow>>(`/api/admin/logs${qs({ q, action, actorType, status, page })}`),
	})

	function reset<T>(setter: (v: T) => void) {
		return (value: T) => {
			setter(value)
			setPage(1)
		}
	}

	return (
		<>
			<PageHeader title="Logs" subtitle="Staff actions, customer activity and billing events in one timeline." />

			<div className="card">
				<div className="flex flex-wrap gap-2 border-b border-line p-3">
					<div className="relative min-w-0 flex-1 sm:max-w-xs">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
						<input className="input pl-9" placeholder="Search summary or actor" value={search} onChange={(e) => reset(setSearch)(e.target.value)} />
					</div>
					<select className="input w-auto" value={actorType} onChange={(e) => reset(setActorType)(e.target.value)}>
						<option value="">All actors</option>
						<option value="admin">Admin</option>
						<option value="user">Customer</option>
						<option value="system">System</option>
					</select>
					<select className="input w-auto" value={action} onChange={(e) => reset(setAction)(e.target.value)}>
						<option value="">All actions</option>
						{filters.data?.actions.map((a) => (
							<option key={a} value={a}>{a}</option>
						))}
					</select>
					<select className="input w-auto" value={status} onChange={(e) => reset(setStatus)(e.target.value)}>
						<option value="">Any result</option>
						<option value="success">Success</option>
						<option value="failure">Failure</option>
					</select>
				</div>

				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<DataTable columns={columns} rows={data.rows} emptyMessage="Nothing matches those filters." onRowClick={setSelected} />
						<Pagination page={data.page} limit={data.limit} total={data.total} onPageChange={setPage} />
					</>
				)}
			</div>

			{selected && <LogDetail row={selected} onClose={() => setSelected(null)} />}
		</>
	)
}

function LogDetail({ row, onClose }: { row: ActivityRow; onClose: () => void }) {
	return (
		<div className="fixed inset-0 z-50 flex justify-end">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={onClose} />
			<aside role="dialog" aria-modal className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto bg-surface p-5">
				<div className="mb-4 flex items-start justify-between gap-3">
					<h2 className="font-display text-lg font-semibold">{row.action}</h2>
					<button type="button" className="btn-secondary" onClick={onClose}>Close</button>
				</div>

				<p className="mb-4 text-sm">{row.summary}</p>

				<dl className="grid grid-cols-2 gap-3 text-sm">
					<div><dt className="text-xs text-ink-soft">When</dt><dd>{formatDate(row.createdAt)}</dd></div>
					<div><dt className="text-xs text-ink-soft">Result</dt><dd><StatusBadge value={row.status} /></dd></div>
					<div><dt className="text-xs text-ink-soft">Actor</dt><dd className="truncate">{row.actorEmail}</dd></div>
					<div><dt className="text-xs text-ink-soft">Actor type</dt><dd>{row.actorType}</dd></div>
					<div><dt className="text-xs text-ink-soft">Target</dt><dd className="mono truncate">{row.targetType ?? '—'} {row.targetId ?? ''}</dd></div>
					<div><dt className="text-xs text-ink-soft">IP</dt><dd className="mono">{row.ipAddress ?? '—'}</dd></div>
				</dl>

				{row.error && (
					<div className="mt-4">
						<h3 className="mb-1 text-xs font-medium text-ink-soft">Error</h3>
						<p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{row.error}</p>
					</div>
				)}

				{row.metadata !== null && row.metadata !== undefined && (
					<div className="mt-4">
						<h3 className="mb-1 text-xs font-medium text-ink-soft">Details</h3>
						<pre className="overflow-x-auto rounded-lg bg-canvas p-3 text-xs">{JSON.stringify(row.metadata, null, 2)}</pre>
					</div>
				)}

				{row.userAgent && (
					<div className="mt-4">
						<h3 className="mb-1 text-xs font-medium text-ink-soft">User agent</h3>
						<p className="mono break-words">{row.userAgent}</p>
					</div>
				)}
			</aside>
		</div>
	)
}
